import { useRef } from "react";
import { Delete, Settings } from "@mui/icons-material";
import { Box, IconButton, Paper, Tooltip, Typography } from "@mui/material";
import DescriptionIcon from "@mui/icons-material/Description";
import GridViewOutlinedIcon from "@mui/icons-material/GridViewOutlined";
import HomeWorkOutlinedIcon from "@mui/icons-material/HomeWorkOutlined";
import PetsOutlinedIcon from "@mui/icons-material/PetsOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";

import SquareFootOutlinedIcon from "@mui/icons-material/SquareFootOutlined";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import { colors, alphaVar } from "../../../../ui/colors";
import { useGlobalSnackbar } from "../../../../../context/SnackbarContext";
import { useAppDispatch } from "../../../../../redux/hooks/redux";
import { removeAdvancedQuote } from "../../../../../redux/features/quotes/quoteListSlice";
import { store } from "../../../../../redux/store";
import { shouldBlockAdvancedQuoteCloseForMissingPrice } from "../../helper/quoteServicePricingGate";
import { useDialog } from "../../../../ui/dialogs/useDialog";
import { BaseModalComponent } from "../../../../common/BaseModalComponent";
import { QuoteLayout } from "../../Quote.layout";
import { quoteCreateActions } from "../../../../../redux/features/quotes/quote-create-advanced";

type Props = {
  payload: any;
  index: number;
};

const clean = (value?: unknown) => String(value ?? "").trim();

const getName = (value: any): string => {
  if (!value) return "";

  if (typeof value === "string") {
    return value.trim();
  }

  return clean(value?.name || value?.label || value?.title || value?.type);
};

const formatMoney = (value?: unknown) => {
  const amount = Number(value ?? 0);

  return amount.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });
};

const formatPricingType = (value?: unknown) => {
  const normalized = clean(value);

  if (!normalized) return "One time";

  const labels: Record<string, string> = {
    monthly: "Monthly",
    yearly: "Yearly",
    unique: "One time",
    one_time: "One time",
    per_item: "Per item",
    per_square_foot: "Per square foot",
    per_sqft: "Per square foot",
    per_car_space: "Per car space",
    per_floor: "Per floor",
  };

  const key = normalized.toLowerCase();

  return labels[key] || normalized.replace(/_/g, " ").replace(/\b\w/g, (character) => character.toUpperCase());
};

export const QuoteAdvancedRow = ({ payload, index }: Props) => {
  const dispatch = useAppDispatch();
  const { showSnackbar } = useGlobalSnackbar();
  const { showDialog, DialogComponent } = useDialog();

  const { loadQuoteForEdit, resetSectorWorkflow, resetStep, clearAdvancedListEditContext } = quoteCreateActions;

  const editTriggerRef = useRef<HTMLButtonElement>(null);

  const workflow = payload?.sectorWorkflow || payload?.scopeOfWork || payload;
  const categoryName = getName(workflow?.category) || getName(workflow?.categories) || clean(workflow?.categoryName);

  const placeName =
    getName(workflow?.places) ||
    getName(workflow?.place) ||
    getName(workflow?.typeOfPlace) ||
    getName(workflow?.typeOfPlaces) ||
    clean(workflow?.typeOfPlaceName);
  const originalServiceName =
    getName(workflow?.service) || getName(workflow?.typeOfService) || getName(payload?.service) || clean(workflow?.name) || `Service ${index + 1}`;

  const serviceNameOverride = clean(payload?.serviceNameOverride) || clean(workflow?.serviceNameOverride);

  const displayedServiceName = serviceNameOverride || originalServiceName;

  const hasServiceNameOverride = Boolean(serviceNameOverride);

  const costOverride = workflow?.costOverride || payload?.costOverride || {};

  const total = Number(costOverride?.value ?? costOverride?.price ?? payload?.total ?? payload?.price ?? 0);

  const pricingType = formatPricingType(costOverride?.by);

  const areas = Array.isArray(workflow?.areas) ? workflow.areas : [];

  const activeAreas = areas.filter((area: any) => {
    if (!Array.isArray(area?.squareFootages)) {
      return true;
    }

    return area.squareFootages.some((item: any) => Number(item?.quantity ?? 0) > 0);
  });
  const otherAreas = Array.isArray(workflow?.otherAreas) ? workflow.otherAreas : [];

  const activeOtherAreas = otherAreas.filter((area: any) => {
    if (area?.quantity != null) {
      return Number(area.quantity) > 0;
    }

    if (!Array.isArray(area?.squareFootages)) {
      return false;
    }

    return area.squareFootages.some((item: any) => Number(item?.quantity ?? 0) > 0);
  });
  const objects =
    Array.isArray(workflow?.objects) && workflow.objects.length > 0
      ? workflow.objects
      : Array.isArray(workflow?.additionalObjects)
        ? workflow.additionalObjects
        : [];

  const specialRequests = Array.isArray(workflow?.specialRequests) ? workflow.specialRequests : [];
  const areaNames = activeAreas
    .map((area: any) => getName(area))
    .filter(Boolean)
    .join(", ");

  const otherAreaNames = activeOtherAreas
    .map((area: any) => getName(area))
    .filter(Boolean)
    .join(", ");

  const objectNames = objects
    .map((object: any) => getName(object))
    .filter(Boolean)
    .join(", ");

  const specialRequestNames = specialRequests
    .map((request: any) => getName(request))
    .filter(Boolean)
    .join(", ");
  const squareFeet = clean(workflow?.squareft) || clean(workflow?.squareFootage);

  const notes = clean(payload?.notes) || clean(workflow?.notes);

  const handleEdit = () => {
    dispatch(loadQuoteForEdit(payload));
    editTriggerRef.current?.click?.();
  };

  const handleDelete = () => {
    showDialog({
      type: "confirm",
      title: "Delete Service",
      message: "Are you sure you want to remove this service from the quote?",
      confirmLabel: "Delete",
      cancelLabel: "Cancel",
      onConfirm: () => dispatch(removeAdvancedQuote(payload.id)),
      onCancel: () => {},
    });
  };

  return (
    <>
      {DialogComponent}

      <BaseModalComponent
        onConfirmClose={() => {
          const quoteCreate = store.getState().quoteCreate;

          if (shouldBlockAdvancedQuoteCloseForMissingPrice(quoteCreate)) {
            showSnackbar("Set an agreed price greater than zero on the summary before closing.", "warning", 5000, "top-center");

            return false;
          }

          dispatch(resetSectorWorkflow(undefined));
          dispatch(resetStep(undefined));
          dispatch(clearAdvancedListEditContext(undefined));
        }}
        modalLabel="Editing Quote"
        defaultSizes={{}}
        defaultFullscreen={false}
        modalKey="Edit-Modal-Quote-Advanced"
        elementTrigger={
          <button
            ref={editTriggerRef}
            type="button"
            style={{
              position: "absolute",
              left: -99999,
              top: -99999,
            }}
          />
        }
      >
        <QuoteLayout />
      </BaseModalComponent>

      <Paper
        elevation={0}
        sx={{
          p: 2,
          borderRadius: 2,
          border: `1px solid ${alphaVar(colors.orange.base, 0.12)}`,
          bgcolor: "var(--fx-surface-base, #FFFFFF)",
          transition: "all 150ms ease",
          "&:hover": {
            borderColor: alphaVar(colors.orange.base, 0.3),
            boxShadow: `0 8px 24px ${alphaVar(colors.orange.base, 0.08)}`,
            transform: "translateY(-1px)",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 1.5,
          }}
        >
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.65,
                px: 1,
                py: 0.45,
                mb: 0.75,
                borderRadius: 999,
                bgcolor: alphaVar(colors.orange.base, 0.08),
                color: colors.orange.base,
              }}
            >
              <DescriptionIcon sx={{ fontSize: 14 }} />

              <Typography
                sx={{
                  fontSize: 10.5,
                  fontWeight: 900,
                  textTransform: "uppercase",
                  letterSpacing: 0.4,
                  lineHeight: 1,
                }}
              >
                Advanced service
              </Typography>
            </Box>

            <Typography
              sx={{
                fontSize: 16,
                fontWeight: 800,
                lineHeight: 1.25,
                color: colors.gray?.[800] || "var(--fx-surface-text, #0F172A)",
                overflowWrap: "anywhere",
              }}
            >
              {displayedServiceName}
            </Typography>

            {hasServiceNameOverride && (
              <Typography
                sx={{
                  mt: 0.35,
                  fontSize: 11.5,
                  color: colors.gray?.[500] || "var(--fx-surface-textsecondary, #64748B)",
                  lineHeight: 1.35,
                }}
              >
                <Box component="span" sx={{ fontWeight: 700 }}>
                  Original service:
                </Box>
                {originalServiceName}
              </Typography>
            )}
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
              gap: 0.75,
              flexShrink: 0,
            }}
          >
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: 18,
                lineHeight: 1.2,
                color: colors.orange.base,
                whiteSpace: "nowrap",
              }}
            >
              {formatMoney(total)}
            </Typography>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
              }}
            >
              <Tooltip title="Edit service">
                <IconButton
                  onClick={handleEdit}
                  size="small"
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: 1.5,
                    color: colors.orange.base,
                    bgcolor: alphaVar(colors.orange.base, 0.08),
                    "&:hover": {
                      bgcolor: alphaVar(colors.orange.base, 0.14),
                    },
                  }}
                >
                  <Settings sx={{ fontSize: 17 }} />
                </IconButton>
              </Tooltip>

              <Tooltip title="Delete service">
                <IconButton
                  onClick={handleDelete}
                  size="small"
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: 1.5,
                    color: colors.gray?.[400] || "var(--fx-surface-texttertiary, #94A3B8)",
                    "&:hover": {
                      color: "#e11d48",
                      bgcolor: alphaVar("#e11d48", 0.08),
                    },
                  }}
                >
                  <Delete sx={{ fontSize: 17 }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            mt: 1.25,
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 0.65,
          }}
        >
          {categoryName && (
            <Tooltip title="Service category" arrow placement="top">
              <Box>
                <InfoPill label={categoryName} icon={<CategoryOutlinedIcon />} />
              </Box>
            </Tooltip>
          )}

          {placeName && (
            <Tooltip title="Type of place" arrow placement="top">
              <Box>
                <InfoPill label={placeName} icon={<HomeWorkOutlinedIcon />} />
              </Box>
            </Tooltip>
          )}

          {squareFeet && (
            <Tooltip title="Total property area" arrow placement="top">
              <Box>
                <InfoPill label={`${squareFeet} sq ft`} icon={<SquareFootOutlinedIcon />} />
              </Box>
            </Tooltip>
          )}
        </Box>

        {(activeAreas.length > 0 || activeOtherAreas.length > 0 || objects.length > 0 || specialRequests.length > 0) && (
          <Box
            sx={{
              mt: 1.25,
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, minmax(0, 1fr))",
              },
              gap: 1,
            }}
          >
            {activeAreas.length > 0 && (
              <Tooltip title={areaNames || "No area names"} arrow placement="top">
                <Box>
                  <SummaryCard
                    label="Areas included"
                    value={`${activeAreas.length} ${activeAreas.length === 1 ? "area" : "areas"}`}
                    icon={<GridViewOutlinedIcon sx={{ fontSize: 18 }} />}
                  />
                </Box>
              </Tooltip>
            )}

            {activeOtherAreas.length > 0 && (
              <Tooltip title={otherAreaNames || "No other area names"} arrow placement="top">
                <Box>
                  <SummaryCard
                    label="Other areas"
                    value={`${activeOtherAreas.length} ${activeOtherAreas.length === 1 ? "area" : "areas"}`}
                    icon={<HomeWorkOutlinedIcon sx={{ fontSize: 18 }} />}
                  />
                </Box>
              </Tooltip>
            )}

            {objects.length > 0 && (
              <Tooltip title={objectNames || "No object names"} arrow placement="top">
                <Box>
                  <SummaryCard
                    label="Additional objects"
                    value={`${objects.length} ${objects.length === 1 ? "object" : "objects"}`}
                    icon={<Inventory2OutlinedIcon sx={{ fontSize: 18 }} />}
                  />
                </Box>
              </Tooltip>
            )}

            {specialRequests.length > 0 && (
              <Tooltip title={specialRequestNames || "No request names"} arrow placement="top">
                <Box>
                  <SummaryCard
                    label="Special requests"
                    value={`${specialRequests.length} ${specialRequests.length === 1 ? "request" : "requests"}`}
                    icon={<PetsOutlinedIcon sx={{ fontSize: 18 }} />}
                  />
                </Box>
              </Tooltip>
            )}
          </Box>
        )}

        <Box
          sx={{
            mt: 1.25,
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, minmax(0, 1fr))",
            },
            borderRadius: 1.75,
            bgcolor: alphaVar(colors.orange.base, 0.035),
            border: `1px solid ${alphaVar(colors.orange.base, 0.08)}`,
            overflow: "hidden",
          }}
        ></Box>

        {notes && (
          <Box
            sx={{
              mt: 1.1,
              pt: 1,
              borderTop: `1px solid ${colors.gray?.[100] || "var(--fx-surface-border, #E2E8F0)"}`,
            }}
          >
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 800,
                color: colors.gray?.[500] || "var(--fx-surface-textsecondary, #64748B)",
                mb: 0.3,
              }}
            >
              Notes
            </Typography>

            <Typography
              sx={{
                fontSize: 12.5,
                color: colors.gray?.[700] || "var(--fx-surface-textsecondary, #64748B)",
                lineHeight: 1.45,
                whiteSpace: "pre-wrap",
                overflowWrap: "anywhere",
              }}
            >
              {notes}
            </Typography>
          </Box>
        )}
      </Paper>
    </>
  );
};

type InfoPillProps = {
  label: string;
  icon?: React.ReactNode;
};

const InfoPill = ({ label, icon }: InfoPillProps) => (
  <Box
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 0.5,
      px: 0.9,
      py: 0.4,
      borderRadius: 999,
      bgcolor: "var(--fx-surface-sunken, #F8FAFC)",
      border: `1px solid ${colors.gray?.[100] || "var(--fx-surface-border, #E2E8F0)"}`,
      color: colors.gray?.[600] || "var(--fx-surface-textsecondary, #64748B)",
      fontSize: 11,
      fontWeight: 700,
      lineHeight: 1,
      cursor: "default",
      transition: "all 140ms ease",
      "& svg": {
        fontSize: 14,
        color: colors.orange.base,
        flexShrink: 0,
      },
      "&:hover": {
        bgcolor: alphaVar(colors.orange.base, 0.08),
        borderColor: alphaVar(colors.orange.base, 0.28),
        color: colors.orange.base,
        boxShadow: `0 2px 8px ${alphaVar(colors.orange.base, 0.12)}`,
        transform: "translateY(-1px)",
      },
    }}
  >
    {icon}
    {label}
  </Box>
);

type SummaryCardProps = {
  label: string;
  value: string;
  icon: React.ReactNode;
};

const SummaryCard = ({ label, value, icon }: SummaryCardProps) => (
  <Box
    sx={{
      p: 1,
      borderRadius: 2,
      bgcolor: "var(--fx-surface-sunken, #F8FAFC)",
      border: `1px solid ${colors.gray?.[100] || "var(--fx-surface-border, #E2E8F0)"}`,
      display: "flex",
      alignItems: "center",
      gap: 1,
      minWidth: 0,
      cursor: "default",
      transition: "all 150ms ease",
      "&:hover": {
        bgcolor: alphaVar(colors.orange.base, 0.05),
        borderColor: alphaVar(colors.orange.base, 0.24),
        transform: "translateY(-1px)",
        boxShadow: `0 4px 12px ${alphaVar(colors.orange.base, 0.08)}`,
      },
    }}
  >
    <Box
      sx={{
        width: 32,
        height: 32,
        borderRadius: 1.75,
        color: colors.orange.base,
        bgcolor: alphaVar(colors.orange.base, 0.08),
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>

    <Box sx={{ minWidth: 0 }}>
      <Typography
        sx={{
          fontSize: 10,
          fontWeight: 800,
          textTransform: "uppercase",
          letterSpacing: 0.5,
          color: colors.gray?.[400] || "var(--fx-surface-texttertiary, #94A3B8)",
        }}
      >
        {label}
      </Typography>

      <Typography
        sx={{
          mt: 0.25,
          fontSize: 12.5,
          fontWeight: 800,
          color: colors.gray?.[800] || "var(--fx-surface-text, #0F172A)",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {value}
      </Typography>
    </Box>
  </Box>
);
